import { format, isSameDay, isThisYear, isToday, isYesterday } from 'date-fns';
import {
  EventType,
  KnownMembership,
  MatrixEvent,
  NotificationCountType,
  Room,
  RoomMember,
} from 'matrix-js-sdk/src/matrix';

import { MeetingResponse } from 'api/meedioBackend/types';
import { CustomMeedioMatrixEvents, isSystemUser, WORKSPACE_KICK_REASON } from 'utils/matrixUtils';

import { UNREAD_SEPARATOR_ID } from './ChatTimelineSeparator';

export const USER_STATUS_GROUP_WINDOW_MS = 60_000;
const MEETING_SESSION_GROUP_WINDOW_MS = 4 * 60 * 60 * 1000;

// Within this window, reorderLeavesBeforeEnds / reorderJoinsBeforeEnds enforce
// chronological ordering (leave/join precedes end) for the same meeting_room_id.
// Outside it, homeserver timestamps are trusted as-is.
const REORDER_LIFECYCLE_WINDOW_MS = 5_000;

export type UserStatusActionType =
  | 'left'
  | 'joined'
  | 'added'
  | 'kicked'
  | 'admin'
  | 'archived'
  | 'started-meeting'
  | 'schedule-meeting'
  | 'joined-meeting'
  | 'left-meeting'
  | 'ended-meeting'
  | 'workspace-kicked';

const GROUPABLE_ACTION_TYPES = new Set<UserStatusActionType>([
  'left',
  'joined',
  'added',
  'joined-meeting',
  'left-meeting',
]);

export type SeparatorLabel = { type: 'i18nKey'; key: string } | { type: 'formatted'; text: string };

export type TimelineItem =
  | { type: 'message'; event: MatrixEvent; key: string; index: number }
  | { type: 'date-separator'; label: SeparatorLabel; key: string; timestamp: number }
  | { type: 'unread-separator'; key: string }
  | {
      type: 'user-status-event';
      actionType: UserStatusActionType;
      events: MatrixEvent[];
      users: RoomMember[];
      actorName?: string;
      key: string;
    }
  | {
      type: 'meeting-started-synthetic';
      key: string;
      timestamp: number;
      meetingRoomId: string;
      resolved: {
        meetingName: string;
        startedByName?: string;
        isInstant: boolean;
      } | null;
    }
  | {
      type: 'scheduled-meeting-synthetic';
      key: string;
      timestamp: number;
      meeting: MeetingResponse;
    };

export const formatSeparatorLabel = (date: Date): SeparatorLabel => {
  if (isToday(date)) return { type: 'i18nKey', key: 'messenger.timeline.today' };
  if (isYesterday(date)) return { type: 'i18nKey', key: 'messenger.timeline.yesterday' };
  if (isThisYear(date)) return { type: 'formatted', text: format(date, 'cccc, LLLL d') };
  return { type: 'formatted', text: format(date, 'LLLL d, yyyy') };
};

const SCROLL_UP_THRESHOLD_PX = 50;

export const getIsScrolledUp = (container: HTMLDivElement): boolean => {
  const { scrollHeight, scrollTop, clientHeight } = container;
  return scrollHeight - scrollTop - clientHeight > SCROLL_UP_THRESHOLD_PX;
};

export const getFirstUnreadEventId = (timeline: MatrixEvent[], room: Room, myUserId: string): string | null => {
  const unreadCount = room.getRoomUnreadNotificationCount(NotificationCountType.Total);
  if (unreadCount <= 0) return null;

  const lastReadEventId = room.getEventReadUpTo(myUserId);
  let startIndex = 0;

  if (lastReadEventId) {
    const lastReadIdx = timeline.findIndex((e) => e.getId() === lastReadEventId);
    // Last read event is beyond the loaded timeline — all loaded messages are unread
    startIndex = lastReadIdx === -1 ? 0 : lastReadIdx + 1;
  }

  for (let i = startIndex; i < timeline.length; i++) {
    if (timeline[i].getSender() !== myUserId) return timeline[i].getId() ?? null;
  }

  return null;
};

export const getIsSeparatorInView = (container: HTMLDivElement): boolean => {
  const element = container.querySelector(`#${UNREAD_SEPARATOR_ID}`);
  if (!element) return false;
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
};

interface ClassifyMemberResult {
  actionType: UserStatusActionType;
  userId: string;
  actorName?: string;
}

export const classifyMemberEvent = (event: MatrixEvent, room: Room): ClassifyMemberResult | null => {
  if (event.getType() !== EventType.RoomMember) return null;

  const membership = event.getContent()?.membership as KnownMembership | undefined;
  const prevMembership = event.getPrevContent()?.membership as KnownMembership | undefined;
  const sender = event.getSender() ?? '';
  const stateKey = event.getStateKey() ?? '';
  if (isSystemUser(stateKey)) return null;

  const actorMember = room.getMember(sender);
  const actorName = actorMember?.rawDisplayName ?? sender;

  const isSelf = sender === stateKey;

  if (membership === KnownMembership.Ban) return null;
  if (membership === KnownMembership.Leave && prevMembership === KnownMembership.Invite) return null; // rejected invite
  if (membership === KnownMembership.Join && prevMembership === KnownMembership.Join) return null; // re-join

  if (membership === KnownMembership.Invite) {
    return { actionType: 'added', userId: stateKey, actorName };
  }

  if (membership === KnownMembership.Leave) {
    if (isSelf) return { actionType: 'left', userId: stateKey };
    const reason = event.getContent()?.reason as string | undefined;
    if (reason === WORKSPACE_KICK_REASON) {
      // No actorName — the sender is the workspaces-bot, intentionally not displayed.
      return { actionType: 'workspace-kicked', userId: stateKey };
    }
    return { actionType: 'kicked', userId: stateKey, actorName };
  }

  if (membership === KnownMembership.Join) {
    return { actionType: 'joined', userId: stateKey };
  }

  return null;
};

interface ClassifyPowerLevelsResult {
  actionType: 'admin';
  userId: string;
}

export const classifyPowerLevelsEvent = (event: MatrixEvent): ClassifyPowerLevelsResult[] => {
  if (event.getType() !== EventType.RoomPowerLevels) return [];

  const prevContent = event.getPrevContent();
  // No previous power_levels content means this is the room creation bootstrap event.
  // The creator starts at 100 by default — this is not a meaningful promotion to show.
  if (!prevContent || prevContent.users === undefined) return [];

  const prevUsers = (prevContent.users ?? {}) as Record<string, number>;
  const newUsers = (event.getContent()?.users ?? {}) as Record<string, number>;

  const results: ClassifyPowerLevelsResult[] = [];

  for (const userId of Object.keys(newUsers)) {
    if (isSystemUser(userId)) continue;

    const prev = prevUsers[userId] ?? 0;
    const next = newUsers[userId] ?? 0;
    if (prev < 100 && next >= 100) {
      results.push({ actionType: 'admin', userId });
    }
  }

  return results;
};

const dedupMembers = (members: RoomMember[]): RoomMember[] => {
  const seen = new Set<string>();
  const out: RoomMember[] = [];
  for (const m of members) {
    if (seen.has(m.userId)) continue;
    seen.add(m.userId);
    out.push(m);
  }
  return out;
};

export const groupConsecutiveStatusEvents = (items: TimelineItem[]): TimelineItem[] => {
  const result: TimelineItem[] = [];

  for (const item of items) {
    if (item.type !== 'user-status-event' || !GROUPABLE_ACTION_TYPES.has(item.actionType)) {
      result.push(item);
      continue;
    }

    const isMeetingLifecycleActionType = item.actionType === 'joined-meeting' || item.actionType === 'left-meeting';
    const windowMs = isMeetingLifecycleActionType ? MEETING_SESSION_GROUP_WINDOW_MS : USER_STATUS_GROUP_WINDOW_MS;

    const last = result[result.length - 1];

    // BUG-004: for meeting-lifecycle types only, require that the incoming
    // event's `meeting_room_id` matches the group's first event. Otherwise
    // two leaves (or joins) from different concurrent calls in the same
    // chat (scheduled + instant) merge into one misleading row. Chat-only
    // types short-circuit to `true` — behaviour unchanged.
    const sameMeetingRoom =
      !isMeetingLifecycleActionType ||
      (last?.type === 'user-status-event' && getMeetingRoomIdFromItem(last) === getMeetingRoomIdFromItem(item));

    if (
      last?.type === 'user-status-event' &&
      last.actionType === item.actionType &&
      sameMeetingRoom &&
      item.events[0].getTs() - last.events[last.events.length - 1].getTs() <= windowMs
    ) {
      const mergedUsers = isMeetingLifecycleActionType
        ? dedupMembers([...last.users, ...item.users])
        : [...last.users, ...item.users];
      result[result.length - 1] = {
        ...last,
        events: [...last.events, ...item.events],
        users: mergedUsers,
        key: last.key,
      };
    } else {
      result.push(item);
    }
  }

  return result;
};

type UserStatusTimelineItem = Extract<TimelineItem, { type: 'user-status-event' }>;

export const stripSystemUsersFromStatusItems = (items: UserStatusTimelineItem[]): UserStatusTimelineItem[] =>
  items
    .map((item) => ({ ...item, users: item.users.filter((member) => !isSystemUser(member.userId)) }))
    .filter((item) => item.users.length > 0);

const MEETING_LIFECYCLE_DEDUPE_TYPES = new Set<string>([
  CustomMeedioMatrixEvents.MEETING_ENDED,
  CustomMeedioMatrixEvents.USER_LEFT_MEETING,
]);

/**
 * Remove MEETING_ENDED items that are "invalidated" by a later USER_JOINED_MEETING
 * for the same meeting_room_id.
 *
 * Race scenario:
 *   T=100  Alice leaves → MEETING_ENDED sent immediately (or delayed event fires)
 *   T=105  Bob joins    → USER_JOINED_MEETING sent
 *   T=110  Bob is still in the meeting
 *
 * Alice's MEETING_ENDED is technically correct (the meeting was momentarily empty)
 * but showing it while Bob is active confuses users.  Suppressing it here lets
 * Bob's own MEETING_ENDED (when Bob eventually leaves) reach the dedupe step and
 * appear as the final visible "Meeting ended" row.
 *
 * This runs *before* dedupe so the suppressed event does not consume the shared
 * dedupe_key and block the later, valid MEETING_ENDED from showing.
 */
const suppressObsoleteMeetingEnded = (
  items: UserStatusTimelineItem[],
  latestStartedTsByRoom?: Map<string, number>
): UserStatusTimelineItem[] =>
  items.filter((item) => {
    if (item.actionType !== 'ended-meeting') return true;

    const ev = item.events[0];
    if (!ev) return true;

    const content = ev.getContent() ?? {};
    const meetingRoomId = content.meeting_room_id as string | undefined;
    if (!meetingRoomId) return true;

    // Explicit ends (host clicked End from sidebar, or last-participant hang-up):
    // ended_by is set and source is not the homeserver delayed-event cleanup.
    // Always render — even when a spurious later MEETING_STARTED exists (BUG-002).
    const isExplicitEnd = !!(content.ended_by as string | undefined) && content.source !== 'delayed_event';
    if (isExplicitEnd) return true;

    // Stale event: a newer MEETING_STARTED for the same room follows this end.
    // Drop delayed/empty-room ends only — never explicit host ends (handled above).
    const latestStartedTs = latestStartedTsByRoom?.get(meetingRoomId);
    if (latestStartedTs !== undefined && latestStartedTs > ev.getTs()) return false;

    const endTs = ev.getTs();

    // Track the most-recent join and leave timestamp per sender for this meeting,
    // considering only events that occurred before the end event.
    // A participant is "active" when their last join timestamp is later than
    // their last leave timestamp (or they have no leave at all).
    // This correctly handles delayed LEFT events that fire after a newer participant
    // joined: e.g. JOINED(B, t=5) → LEFT(A-delayed, t=60) → MEETING_ENDED(t=90).
    // The boundary approach would miss B, but per-user tracking shows B is active.
    const lastJoinTs = new Map<string, number>();
    const lastLeftTs = new Map<string, number>();

    for (const it of items) {
      if (it.actionType !== 'joined-meeting' && it.actionType !== 'left-meeting') continue;
      for (const itEv of it.events) {
        if ((itEv.getContent()?.meeting_room_id as string | undefined) !== meetingRoomId) continue;
        const ts = itEv.getTs();
        if (ts >= endTs) continue;
        const sender = itEv.getSender();
        if (!sender) continue;
        if (it.actionType === 'joined-meeting') {
          if (ts > (lastJoinTs.get(sender) ?? -1)) lastJoinTs.set(sender, ts);
        } else {
          if (ts > (lastLeftTs.get(sender) ?? -1)) lastLeftTs.set(sender, ts);
        }
      }
    }

    const hasActiveParticipant = Array.from(lastJoinTs.entries()).some(
      ([sender, joinTs]) => joinTs > (lastLeftTs.get(sender) ?? -1)
    );

    return !hasActiveParticipant;
  });

export const dedupeMeetingLifecycleUserStatusItems = (
  items: UserStatusTimelineItem[],
  latestStartedTsByRoom?: Map<string, number>
): UserStatusTimelineItem[] => {
  const withoutObsolete = suppressObsoleteMeetingEnded(items, latestStartedTsByRoom);

  const seenKeys = new Set<string>();
  const result: UserStatusTimelineItem[] = [];

  for (const item of withoutObsolete) {
    const [ev] = item.events;
    if (!ev) {
      result.push(item);
      continue;
    }

    const eventType = ev.getType();
    if (!MEETING_LIFECYCLE_DEDUPE_TYPES.has(eventType)) {
      result.push(item);
      continue;
    }

    const dedupeKey = ev.getContent()?.dedupe_key as string | undefined;
    if (!dedupeKey) {
      result.push(item);
      continue;
    }

    if (seenKeys.has(dedupeKey)) continue;
    seenKeys.add(dedupeKey);
    result.push(item);
  }

  return result;
};

const startOfDayMs = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const buildTimelineItems = (timeline: MatrixEvent[]): TimelineItem[] => {
  const items: TimelineItem[] = [];
  let lastSeparatorDate: Date | null = null;

  for (let i = 0; i < timeline.length; i++) {
    const event = timeline[i];
    const eventId = event.getId();
    const eventDate = new Date(event.getTs());

    if (!lastSeparatorDate || !isSameDay(eventDate, lastSeparatorDate)) {
      const label = formatSeparatorLabel(eventDate);
      items.push({ type: 'date-separator', label, key: `date-sep-${eventId}`, timestamp: startOfDayMs(eventDate) });
      lastSeparatorDate = eventDate;
    }

    items.push({ type: 'message', event, key: eventId ?? `msg-${event.getTs()}`, index: i });
  }

  return items;
};

export const insertUnreadSeparatorBeforeMessage = (
  items: TimelineItem[],
  firstUnreadEventId: string | null
): TimelineItem[] => {
  if (!firstUnreadEventId) return items;

  const idx = items.findIndex((item) => item.type === 'message' && item.event.getId() === firstUnreadEventId);
  if (idx === -1) return items;

  const next = [...items];
  next.splice(idx, 0, { type: 'unread-separator', key: UNREAD_SEPARATOR_ID });
  return next;
};

type DateSeparatorItem = Extract<TimelineItem, { type: 'date-separator' }>;

const getItemTimestamp = (item: TimelineItem): number => {
  if (item.type === 'message') return item.event.getTs();
  if (item.type === 'user-status-event') return item.events[0].getTs();
  if (item.type === 'date-separator') return item.timestamp;
  if (item.type === 'meeting-started-synthetic') return item.timestamp;
  if (item.type === 'scheduled-meeting-synthetic') return item.timestamp;
  return -1;
};

type SyntheticMeetingStartItem = Extract<TimelineItem, { type: 'meeting-started-synthetic' }>;

/**
 * Insert synthetic meeting-started rows (FR-4 backfill) into an already-merged timeline
 * by their timestamp. Each synthetic's `timestamp` is `endEvent.ts − duration*1000` so
 * it falls immediately before its paired end row.
 */
export const mergeSyntheticMeetingStartsIntoTimeline = (
  timelineItems: TimelineItem[],
  syntheticItems: SyntheticMeetingStartItem[]
): TimelineItem[] => {
  if (!syntheticItems.length) return timelineItems;

  const result = [...timelineItems];

  for (const syntheticItem of [...syntheticItems].sort((a, b) => a.timestamp - b.timestamp)) {
    const ts = syntheticItem.timestamp;
    let insertIdx = result.length;
    for (let i = result.length - 1; i >= 0; i--) {
      const itemTs = getItemTimestamp(result[i]);
      if (itemTs !== -1 && itemTs <= ts) {
        insertIdx = i + 1;
        break;
      }
      if (itemTs !== -1 && itemTs > ts) {
        insertIdx = i;
      }
    }
    result.splice(insertIdx, 0, syntheticItem);
  }

  return result;
};

type SyntheticScheduledMeetingItem = Extract<TimelineItem, { type: 'scheduled-meeting-synthetic' }>;

/** Insert backend-sourced schedule cards when the Matrix timeline event is missing (E2EE pre-join). */
export const mergeSyntheticScheduledMeetingsIntoTimeline = (
  timelineItems: TimelineItem[],
  syntheticItems: SyntheticScheduledMeetingItem[]
): TimelineItem[] => {
  if (!syntheticItems.length) return timelineItems;

  const result = [...timelineItems];

  for (const syntheticItem of [...syntheticItems].sort((a, b) => a.timestamp - b.timestamp)) {
    const ts = syntheticItem.timestamp;
    let insertIdx = result.length;
    for (let i = result.length - 1; i >= 0; i--) {
      const itemTs = getItemTimestamp(result[i]);
      if (itemTs !== -1 && itemTs <= ts) {
        insertIdx = i + 1;
        break;
      }
      if (itemTs !== -1 && itemTs > ts) {
        insertIdx = i;
      }
    }
    result.splice(insertIdx, 0, syntheticItem);

    const eventDate = new Date(ts);
    let precedingSep: DateSeparatorItem | null = null;
    for (let j = insertIdx - 1; j >= 0; j--) {
      if (result[j].type === 'date-separator') {
        precedingSep = result[j] as DateSeparatorItem;
        break;
      }
    }
    if (!precedingSep || !isSameDay(new Date(precedingSep.timestamp), eventDate)) {
      const dayTs = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()).getTime();
      const sep: DateSeparatorItem = {
        type: 'date-separator',
        label: formatSeparatorLabel(eventDate),
        key: `date-sep-scheduled-${syntheticItem.key}`,
        timestamp: dayTs,
      };
      result.splice(insertIdx, 0, sep);
    }
  }

  return result;
};

// Read `meeting_room_id` from a timeline item's first event. Returns undefined for
// any item that is not a user-status-event, so meeting-lifecycle guards short-circuit
// safely on chat-only rows.
const getMeetingRoomIdFromItem = (item: TimelineItem): string | undefined => {
  if (item.type !== 'user-status-event') return undefined;
  return (item.events[0]?.getContent?.()?.meeting_room_id as string | undefined) ?? undefined;
};

const isLeaveLifecycleItem = (item: TimelineItem): boolean =>
  item.type === 'user-status-event' && (item.actionType === 'left-meeting' || item.actionType === 'left');

// BUG-069: a join is a real user action that must stay visible. Used by the
// join-before-end reorder (mirror of the leave reorder).
const isJoinLifecycleItem = (item: TimelineItem): boolean =>
  item.type === 'user-status-event' && item.actionType === 'joined-meeting';

const isEndedMeetingItem = (item: TimelineItem): boolean =>
  item.type === 'user-status-event' && item.actionType === 'ended-meeting';

/**
 * Enforce "matching lifecycle event precedes end for the same meeting_room_id"
 * within REORDER_LIFECYCLE_WINDOW_MS. For each ended-meeting item, look forward
 * for every item satisfying `isMatch` whose meeting_room_id matches and whose
 * timestamp is within the window, and move all such matches to immediately before
 * the ended-meeting, preserving their original relative order. Pairs outside the
 * window and pairs from different meetings are untouched. Single forward pass;
 * O(n * w) where w is the lookahead window in item count.
 *
 * Leaves (BUG-008) and joins (BUG-069) both use this — the homeserver can assign
 * a leave/join a slightly later origin_server_ts than the end it logically precedes.
 */
const reorderLifecycleEventsBeforeEnds = (
  items: TimelineItem[],
  isMatch: (item: TimelineItem) => boolean
): TimelineItem[] => {
  const result = items.slice();
  for (let i = 0; i < result.length; i++) {
    const endItem = result[i];
    if (!isEndedMeetingItem(endItem)) continue;
    const endTs = getItemTimestamp(endItem);
    if (endTs < 0) continue;
    const endMeetingRoomId = getMeetingRoomIdFromItem(endItem);
    if (!endMeetingRoomId) continue;

    const matchIdxs: number[] = [];
    for (let j = i + 1; j < result.length; j++) {
      const candidate = result[j];
      const candidateTs = getItemTimestamp(candidate);
      if (candidateTs < 0) continue;
      if (candidateTs - endTs > REORDER_LIFECYCLE_WINDOW_MS) break;
      if (!isMatch(candidate)) continue;
      if (getMeetingRoomIdFromItem(candidate) !== endMeetingRoomId) continue;
      matchIdxs.push(j);
    }

    if (!matchIdxs.length) continue;

    // Snapshot the matches in original order, then remove them (highest index
    // first so earlier indices stay valid). All matches are after `i`, so the
    // ended-meeting's index is unaffected by the removals.
    const moved = matchIdxs.map((idx) => result[idx]);
    for (let k = matchIdxs.length - 1; k >= 0; k--) {
      result.splice(matchIdxs[k], 1);
    }
    result.splice(i, 0, ...moved);
    // Re-anchor `i` on the (now shifted) ended-meeting so the next iteration
    // continues past this fixed group without re-processing the moved items.
    i += moved.length;
  }
  return result;
};

const reorderLeavesBeforeEnds = (items: TimelineItem[]): TimelineItem[] =>
  reorderLifecycleEventsBeforeEnds(items, isLeaveLifecycleItem);

const reorderJoinsBeforeEnds = (items: TimelineItem[]): TimelineItem[] =>
  reorderLifecycleEventsBeforeEnds(items, isJoinLifecycleItem);

// BUG-080: a MEETING_STARTED renders as a `message` item (MessengerMessage →
// MeetingStartedTimelineItem), not a user-status item, so getMeetingRoomIdFromItem
// does not see it. These helpers read the started event's meeting_room_id so the
// start↔join reorder can match it against a joined-meeting status item.
const isMeetingStartedMessageItem = (item: TimelineItem): boolean =>
  item.type === 'message' && item.event.getType?.() === CustomMeedioMatrixEvents.MEETING_STARTED;

const getStartMessageMeetingRoomId = (item: TimelineItem): string | undefined => {
  if (item.type !== 'message') return undefined;
  return (item.event.getContent?.()?.meeting_room_id as string | undefined) ?? undefined;
};

/**
 * BUG-080 (mirror of reorderJoinsBeforeEnds): a `MEETING_STARTED` must precede the
 * `USER_JOINED_MEETING` for the same meeting_room_id. On a fast start the homeserver
 * can assign the join an origin_server_ts <= the start's, so the join sorts before
 * the start and renders as "joined the video meeting" above "started a new video
 * meeting". For each `joined-meeting` item, look forward within
 * REORDER_LIFECYCLE_WINDOW_MS for a `MEETING_STARTED` message item with the same
 * meeting_room_id and move it to immediately before the join. Cross-meeting pairs,
 * out-of-window pairs, and already-correctly-ordered pairs (start before join, so
 * not found in the forward scan) are untouched.
 */
const reorderStartsBeforeJoins = (items: TimelineItem[]): TimelineItem[] => {
  // Fast path: the reorder only matters when both a join and a MEETING_STARTED
  // message item are present. Most timelines have at most one of those (or neither),
  // so this single O(n) scan lets us skip the array copy + nested forward scan below.
  let hasJoin = false;
  let hasStartMessage = false;
  for (const item of items) {
    if (!hasJoin && isJoinLifecycleItem(item)) hasJoin = true;
    else if (!hasStartMessage && isMeetingStartedMessageItem(item)) hasStartMessage = true;
    if (hasJoin && hasStartMessage) break;
  }
  if (!hasJoin || !hasStartMessage) return items;

  const result = items.slice();
  for (let i = 0; i < result.length; i++) {
    const joinItem = result[i];
    if (!isJoinLifecycleItem(joinItem)) continue;
    const joinTs = getItemTimestamp(joinItem);
    if (joinTs < 0) continue;
    const joinMeetingRoomId = getMeetingRoomIdFromItem(joinItem);
    if (!joinMeetingRoomId) continue;

    for (let j = i + 1; j < result.length; j++) {
      const candidate = result[j];
      const candidateTs = getItemTimestamp(candidate);
      if (candidateTs < 0) continue;
      if (candidateTs - joinTs > REORDER_LIFECYCLE_WINDOW_MS) break;
      if (!isMeetingStartedMessageItem(candidate)) continue;
      if (getStartMessageMeetingRoomId(candidate) !== joinMeetingRoomId) continue;
      // Move the start to immediately before the join, preserving logical order.
      const [startItem] = result.splice(j, 1);
      result.splice(i, 0, startItem);
      // The join shifted right by one; advance past the relocated start so the next
      // iteration continues from the join.
      i++;
      break;
    }
  }
  return result;
};

/**
 * Source precedence for MEETING_ENDED events. Higher is better.
 * `immediate` and `banner` carry meeting_name and are host/sidebar-initiated.
 * `banner_cleanup` is auto-generated and lacks meeting_name.
 * Events with no `source` field are legacy backend delayed events (also lack meeting_name).
 * `delayed_event` is the homeserver stale-fallback (normally filtered elsewhere).
 */
const ENDED_SOURCE_PRECEDENCE: Record<string, number> = {
  immediate: 3,
  banner: 3,
  banner_cleanup: 2,
  delayed_event: 0,
};

const getEndedMeetingSourcePrecedence = (item: TimelineItem): number => {
  if (item.type !== 'user-status-event') return -1;
  const source = item.events[0]?.getContent?.()?.source as string | undefined;
  // Events without a source field (legacy backend delayed events) get precedence 1 —
  // above 'delayed_event' (0) but below 'banner_cleanup' (2).
  return ENDED_SOURCE_PRECEDENCE[source ?? ''] ?? 1;
};

/**
 * BUG-046 / BUG-047: collapse multiple `ended-meeting` rows for the same
 * `meeting_room_id` into the single highest-precedence entry.
 *
 * Root causes:
 *  - BUG-046: backend-scheduled Matrix delayed events fire at `ends_at` for every
 *    participant who registered them at join time. They carry no `source` field and
 *    no `meeting_name`, appearing as "Video meeting has ended · 00:00:00" even when
 *    the meeting was already explicitly ended earlier.
 *  - BUG-047: the banner auto-cleanup 8-second timer fires independently on every
 *    client that had the banner open, producing a `banner_cleanup` MEETING_ENDED
 *    event from each within seconds of each other.
 *
 * Strategy: keep the highest-precedence entry per `meeting_room_id`; when two
 * entries share the same precedence, keep the earliest (lower timestamp wins).
 * The kept entry preserves its original `meeting_name` and duration.
 */
export const dropDuplicateEndedMeetingRows = (items: TimelineItem[]): TimelineItem[] => {
  type BestEntry = { idx: number; precedence: number; ts: number };
  const bestByRoom = new Map<string, BestEntry>();
  const toRemove = new Set<number>();

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!isEndedMeetingItem(item)) continue;

    const roomId = getMeetingRoomIdFromItem(item);
    if (!roomId) continue;

    const precedence = getEndedMeetingSourcePrecedence(item);
    const ts = getItemTimestamp(item);
    const existing = bestByRoom.get(roomId);

    if (!existing) {
      bestByRoom.set(roomId, { idx: i, precedence, ts });
    } else if (precedence > existing.precedence || (precedence === existing.precedence && ts < existing.ts)) {
      // Current entry is better → drop the previously-recorded one
      toRemove.add(existing.idx);
      bestByRoom.set(roomId, { idx: i, precedence, ts });
    } else {
      // Existing entry is equal or better → drop current
      toRemove.add(i);
    }
  }

  if (toRemove.size === 0) return items;
  return items.filter((_, idx) => !toRemove.has(idx));
};

/**
 * BUG-059 (covers BUG-060 join-after-end + BUG-061 delayed leave after end):
 *
 * Drop ALL `joined-meeting` and `left-meeting` items for a meeting_room_id that
 * arrive AFTER the corresponding `ended-meeting` row within
 * POST_END_LIFECYCLE_DROP_WINDOW_MS (5 minutes). Root causes:
 *
 *  1. MEETING_LIFECYCLE_PARTICIPANT_LEFT_DELAY_MS = 60 s. Homeserver delayed
 *     USER_LEFT_MEETING fires 60 s after the user left — often well after the
 *     meeting ended (e.g. a 9-second instant call). These appear as spurious
 *     "left" rows below the "ended" row.
 *  2. For very short meetings the homeserver can assign origin_server_ts to
 *     USER_JOINED_MEETING slightly after MEETING_ENDED, placing join events
 *     after the end row without a reorder handler to fix it.
 *  3. Pre-Wave-10 delayed events carry the old `is_scheduled: true` payload and
 *     still show the meeting name — they should be suppressed entirely once the
 *     meeting is over.
 *
 * The 5-minute window is:
 *  - Larger than the 60-second delayed-event max, so all stale delayed events
 *    are caught.
 *  - Much smaller than the gap between recurring occurrences (hours/days), so
 *    events from the next occurrence are never dropped.
 */
const POST_END_LIFECYCLE_DROP_WINDOW_MS = 5 * 60 * 1000;

const dropPostEndLifecycleEvents = (items: TimelineItem[]): TimelineItem[] => {
  const endTsByRoom = new Map<string, number>();
  const toRemove = new Set<number>();

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const roomId = getMeetingRoomIdFromItem(item);
    if (!roomId) continue;

    if (isEndedMeetingItem(item)) {
      const ts = getItemTimestamp(item);
      // BUG-006 Fix C: track the latest end per room so post-end leave drops use
      // the most recent session boundary (not an earlier duplicate end row).
      const existing = endTsByRoom.get(roomId);
      if (existing === undefined || ts > existing) endTsByRoom.set(roomId, ts);
      continue;
    }

    // BUG-069: only stale post-end LEAVES are dropped here. Joins are real user
    // actions and must stay visible — a join that lands just after the end row is
    // pulled back before it by `reorderJoinsBeforeEnds`; a join well after the end
    // belongs to a new session and is left in place. Previously this also dropped
    // joins (BUG-059/060), which hid legitimate "X joined" rows from everyone.
    const isLeaveItem = item.type === 'user-status-event' && item.actionType === 'left-meeting';
    if (!isLeaveItem) continue;

    const endTs = endTsByRoom.get(roomId);
    if (endTs === undefined) continue;

    const itemTs = getItemTimestamp(item);
    // Drop if the leave arrived after the end AND within the stale-delay window.
    if (itemTs > endTs && itemTs - endTs < POST_END_LIFECYCLE_DROP_WINDOW_MS) {
      toRemove.add(i);
    }
  }

  if (toRemove.size === 0) return items;
  return items.filter((_, idx) => !toRemove.has(idx));
};

/**
 * BUG-026 / BUG-064: drop a duplicate `USER_LEFT_MEETING` row that shares its
 * `dedupe_key` with an earlier leave row for the same meeting. The last
 * participant's hangup sends an immediate `USER_LEFT_MEETING` and cancels the
 * homeserver-side delayed one — but if the cancel loses the race, the delayed
 * event fires anyway (BUG-026: after `MEETING_ENDED`; BUG-064: even while the
 * meeting is still open). Both copies share the same `dedupe_key` (it encodes the
 * per-join `occurredAt` session timestamp), so the second occurrence is dropped
 * regardless of meeting-end status. A genuine leave-and-rejoin gets a NEW
 * `occurredAt` → new key → both leaves are kept.
 */
const dropPostEndDuplicateLeaves = (items: TimelineItem[]): TimelineItem[] => {
  // Track, per meeting_room_id, the dedupe_keys of leave rows seen so far.
  const seenLeaveKeysByMeeting = new Map<string, Set<string>>();
  const dropIdxs = new Set<number>();

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const meetingRoomId = getMeetingRoomIdFromItem(item);
    if (!meetingRoomId) continue;

    if (!isLeaveLifecycleItem(item)) continue;

    // BUG-050: a merged leave row carries MULTIPLE events (e.g. "Alexas, 137 left").
    // Only events[0]'s dedupe_key was previously stored, so a post-end delayed leave
    // from the SECOND merged user slipped past the dedup and rendered a spurious row.
    // Collect every event's dedupe_key from the (possibly merged) item.
    const itemDedupeKeys =
      item.type === 'user-status-event'
        ? item.events.map((e) => e.getContent?.()?.dedupe_key as string | undefined).filter((k): k is string => !!k)
        : [];
    const seenKeys = seenLeaveKeysByMeeting.get(meetingRoomId) ?? new Set<string>();

    // BUG-064 (supersedes BUG-026/BUG-050 post-end-only logic): drop ANY leave
    // whose dedupe_key(s) were already seen in an earlier leave for this meeting —
    // regardless of whether the meeting has ended yet. The immediate
    // USER_LEFT_MEETING and the homeserver-fired delayed copy share the same
    // dedupe_key (it encodes the per-join `occurredAt` session timestamp), and the
    // delayed copy can arrive while the meeting is STILL OPEN (the cancel HTTP call
    // lost the race). A genuine leave-and-rejoin produces a NEW `occurredAt` → new
    // key, so it is never wrongly deduplicated.
    if (itemDedupeKeys.length > 0 && itemDedupeKeys.every((k) => seenKeys.has(k))) {
      dropIdxs.add(i);
      continue;
    }
    for (const k of itemDedupeKeys) seenKeys.add(k);
    seenLeaveKeysByMeeting.set(meetingRoomId, seenKeys);
  }

  if (dropIdxs.size === 0) return items;
  return items.filter((_, idx) => !dropIdxs.has(idx));
};

/**
 * Resolve the subject user of a meeting lifecycle event: the explicit
 * `content.user_id` when present, otherwise the event sender.
 */
const getLifecycleSubjectUserId = (ev: MatrixEvent): string | undefined => {
  const content = ev.getContent?.() ?? {};
  const uid = typeof content.user_id === 'string' && content.user_id.length > 0 ? content.user_id : ev.getSender();
  return uid ?? undefined;
};

type MeetingMembershipState = 'joined' | 'left';

/**
 * BUG-066 (Fix B — event-driven, no time window): drop a `USER_LEFT_MEETING`
 * for a user who is ALREADY in the `left` state for that `meeting_room_id` with
 * no intervening join. This removes the misleading case where a user appears in
 * a later "left the meeting" row even though they already left in an earlier row
 * (their homeserver-delayed second leave carries a NEW `dedupe_key`, so BUG-064's
 * dedupe_key dedup does not catch it, and it merges into the next leave wave).
 *
 * The rule is purely event-ordering driven:
 *   - `joined-meeting` → subject user state = 'joined'
 *   - `left-meeting`   → if state already 'left' (no join since) → DROP that
 *                        event; else keep and set state = 'left'.
 *
 * A genuine leave → rejoin → leave emits a `USER_JOINED_MEETING` in between that
 * flips the state back to 'joined', so the second real leave is preserved. No
 * timestamps or windows are consulted — correctness comes from trusted Matrix
 * event order, per the Fix B decision (Fix A time-window shrink was rejected).
 */
const dropRepeatedLeavesWithoutRejoin = (items: TimelineItem[]): TimelineItem[] => {
  const stateByRoom = new Map<string, Map<string, MeetingMembershipState>>();
  const result: TimelineItem[] = [];

  for (const item of items) {
    const roomId = getMeetingRoomIdFromItem(item);
    if (!roomId || item.type !== 'user-status-event') {
      result.push(item);
      continue;
    }

    const userState = stateByRoom.get(roomId) ?? new Map<string, MeetingMembershipState>();

    if (item.actionType === 'joined-meeting') {
      for (const ev of item.events) {
        const uid = getLifecycleSubjectUserId(ev);
        if (uid) userState.set(uid, 'joined');
      }
      stateByRoom.set(roomId, userState);
      result.push(item);
      continue;
    }

    if (item.actionType === 'left-meeting') {
      const keptEvents = item.events.filter((ev) => {
        const uid = getLifecycleSubjectUserId(ev);
        if (!uid) return true; // unidentifiable subject → keep (never lose data)
        return userState.get(uid) !== 'left'; // already left w/o rejoin → drop
      });
      // Every subject of this row is now in the 'left' state (kept or dropped):
      // a later duplicate leave for the same user (no join in between) is dropped.
      for (const ev of item.events) {
        const uid = getLifecycleSubjectUserId(ev);
        if (uid) userState.set(uid, 'left');
      }
      stateByRoom.set(roomId, userState);

      if (keptEvents.length === 0) continue;
      if (keptEvents.length === item.events.length) {
        result.push(item);
        continue;
      }
      const keptUserIds = new Set(keptEvents.map(getLifecycleSubjectUserId).filter((u): u is string => !!u));
      const keptUsers = item.users.filter((u) => keptUserIds.has(u.userId));
      result.push({ ...item, events: keptEvents, users: keptUsers.length > 0 ? keptUsers : item.users });
      continue;
    }

    result.push(item);
  }

  return result;
};

/**
 * BUG-077: decide whether a `MEETING_STARTED` event is a non-primary duplicate.
 *
 * `MEETING_STARTED` is rendered directly by `MessengerMessage` (not through the
 * user-status pipeline), so it has no dedup of its own. Late / duplicate starts
 * (the T2 paired-start emit in `fireScheduledEnd`, refetch trigger loops, or a
 * homeserver-delayed copy) therefore render as extra "started" rows after the
 * meeting already started and ended.
 *
 * Two suppression rules — either is sufficient:
 *
 * Rule A — earlier-start wins: the earliest `MEETING_STARTED` per
 * `(meeting_room_id, session window)` is the primary; every later duplicate
 * within `MEETING_SESSION_GROUP_WINDOW_MS` is superseded. Recurring meetings
 * reuse the same `meeting_room_id` across occurrences, so the window keeps
 * the next occurrence's start visible — chronology guarantees a late duplicate
 * of occurrence N falls inside N's window while occurrence N+1's start is
 * outside it. Equal timestamps break ties on event id so the choice is stable.
 *
 * Rule B — post-explicit-end start: if an explicit host `MEETING_ENDED`
 * (ended_by set, not a delayed_event) precedes this start within the session
 * window and no other `MEETING_STARTED` for the same room exists between that
 * end and this event, the start is a spurious post-end re-announce (e.g. the
 * T1 wall-clock timer firing after an early host end). This rule fires even
 * when the original real start is not yet paginated into the loaded timeline.
 */
export const isSupersededMeetingStarted = (event: MatrixEvent, roomEvents: MatrixEvent[]): boolean => {
  if (event.getType() !== CustomMeedioMatrixEvents.MEETING_STARTED) return false;
  const meetingRoomId = (event.getContent() ?? {}).meeting_room_id as string | undefined;
  if (!meetingRoomId) return false;
  const ts = event.getTs();
  const id = event.getId() ?? '';

  // Rule A: an earlier start exists within the session window → this one is a duplicate.
  for (const other of roomEvents) {
    if (other === event) continue;
    if (other.getType() !== CustomMeedioMatrixEvents.MEETING_STARTED) continue;
    if (((other.getContent() ?? {}).meeting_room_id as string | undefined) !== meetingRoomId) continue;
    const otherTs = other.getTs();
    if (Math.abs(otherTs - ts) > MEETING_SESSION_GROUP_WINDOW_MS) continue;
    if (otherTs < ts) return true;
    if (otherTs === ts && (other.getId() ?? '') < id) return true;
  }

  // Rule B: an explicit MEETING_ENDED precedes this start within the session window.
  // The emit path already blocks re-starts after explicit ends (isRestartAfterEnd guard
  // in notifyScheduledMeetingStarted / announceMeetingLive), so any MEETING_STARTED
  // that follows an explicit end is a spurious post-end re-announce — suppress it.
  // This rule fires even when the original real start is not yet paginated.
  for (const other of roomEvents) {
    if (other === event) continue;
    if (other.getType() !== CustomMeedioMatrixEvents.MEETING_ENDED) continue;
    if (((other.getContent() ?? {}).meeting_room_id as string | undefined) !== meetingRoomId) continue;
    const otherTs = other.getTs();
    if (otherTs >= ts) continue;
    if (ts - otherTs > MEETING_SESSION_GROUP_WINDOW_MS) continue;
    const content = other.getContent() ?? {};
    const isExplicit = !!(content.ended_by as string | undefined) && content.source !== 'delayed_event';
    if (isExplicit) return true;
  }

  return false;
};

export const mergeUserStatusIntoTimeline = (
  timelineItems: TimelineItem[],
  userStatusItems: UserStatusTimelineItem[]
): TimelineItem[] => {
  if (!userStatusItems.length) return timelineItems;

  const result = [...timelineItems];

  for (const statusItem of [...userStatusItems].sort((a, b) => getItemTimestamp(a) - getItemTimestamp(b))) {
    const ts = getItemTimestamp(statusItem);
    let insertIdx = result.length;
    for (let i = result.length - 1; i >= 0; i--) {
      const itemTs = getItemTimestamp(result[i]);
      if (itemTs !== -1 && itemTs <= ts) {
        insertIdx = i + 1;
        break;
      }
      if (itemTs !== -1 && itemTs > ts) {
        insertIdx = i;
      }
    }
    result.splice(insertIdx, 0, statusItem);

    // Inject a date-separator before the status event if its day isn't already covered
    const eventDate = new Date(ts);
    let precedingSep: DateSeparatorItem | null = null;
    for (let j = insertIdx - 1; j >= 0; j--) {
      if (result[j].type === 'date-separator') {
        precedingSep = result[j] as DateSeparatorItem;
        break;
      }
    }
    if (!precedingSep || !isSameDay(new Date(precedingSep.timestamp), eventDate)) {
      const dayTs = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()).getTime();
      const sep: DateSeparatorItem = {
        type: 'date-separator',
        label: formatSeparatorLabel(eventDate),
        key: `date-sep-status-${dayTs}`,
        timestamp: dayTs,
      };
      result.splice(insertIdx, 0, sep);
    }
  }

  // Lifecycle post-processing pipeline (inner-first):
  //   reorderStartsBeforeJoins      — pull a MEETING_STARTED that landed just after its join before it (< 5 s)
  //   reorderJoinsBeforeEnds        — pull a join that landed just after end before it (< 5 s)
  //   reorderLeavesBeforeEnds       — pull a leave that landed just after end before it (< 5 s)
  //   dropPostEndDuplicateLeaves    — drop same-session duplicate leaves (matching dedupe_key)
  //   dropPostEndLifecycleEvents    — drop stale post-end leaves within 5 min
  //   dropDuplicateEndedMeetingRows — keep the highest-precedence ended row per meeting
  //   dropRepeatedLeavesWithoutRejoin — drop a second leave for a user with no intervening join
  //   groupConsecutiveStatusEvents  — merge adjacent same-type rows
  return groupConsecutiveStatusEvents(
    dropRepeatedLeavesWithoutRejoin(
      dropDuplicateEndedMeetingRows(
        dropPostEndLifecycleEvents(
          dropPostEndDuplicateLeaves(reorderLeavesBeforeEnds(reorderJoinsBeforeEnds(reorderStartsBeforeJoins(result))))
        )
      )
    )
  );
};
